import allAbilities from 'data/abitilies'
import IAbilityData from 'data/abitilies/interfaces/IAbilityData'
import IMeasuredMetric from 'data/abitilies/interfaces/IMeasuredMetric'
import { age } from 'data/abitilies/interfaces/INormData'
import metrics from 'data/abitilies/metrics'
import { normativeInfo, stainScale } from 'data/abitilies/normativeInfo'
import { gamesInCurrentTalentMap } from 'data/games/gameTypes'
import IChild from 'types/database/children/child'
import IAbilities from 'types/database/children/child/abilities'
import { getAge } from './utils'

const allMetricswithNormData = [
  metrics.ContinuousPerformanceAttention,
  metrics.NonSymbolicNumericalRepresentation,
  metrics.SymbolicNumericalRepresentation,
  metrics.Enumeration,
  metrics.Planning,
  metrics.Inhibition,
  metrics.SpatialAbilities,
  metrics.VisualSpatialShortTermMemory,
  metrics.VisualSpatialWorkingMemory
]

export const chartAbilties = (
  child: IChild & { [key: string]: any }
): IAbilities => {
  const { assessments, currentAssessment } = child
  const currentTalentMap = assessments[currentAssessment].current_talent_map

  const targetAbilities = allAbilities.filter(a => {
    if (gamesInCurrentTalentMap[a.key]) {
      const val = a.metrics.every(metric => {
        const gameKey =
          metric.storeKey?.replace(/-|\s/gi, '_').toLowerCase() || ''
        return currentTalentMap && currentTalentMap[gameKey] > -1
      })
      return val
    }

    return true
  })

  return formatData(
    targetAbilities,
    currentTalentMap,
    getAge(child.birth_date, Number(currentAssessment))
  )
}

export const updateAbilityData = (
  child: IChild & { [key: string]: any }
): IAbilities => {
  const targetAbilities = allAbilities.filter(a => {
    return a.metrics.filter(m => m.normData).length > 0
  })

  // logger.info('needed abilities: ', targetAbilities)
  const { currentAssessment } = child
  const currentTalentmap =
    child.assessments[currentAssessment]?.current_talent_map
  // logger.info('current talent map: ', currentTalentmap)

  return formatData(
    targetAbilities,
    currentTalentmap,
    getAge(child.birth_date, Number(currentAssessment))
  )
}

const formatData = (
  data: IAbilityData[],
  currentTalentmap,
  childAge: number
): IAbilities => {
  if (!currentTalentmap) {
    return {}
  }

  const values = data.map(ability => {
    const val = getMeanValue(
      ability.key,
      ability.metrics,
      currentTalentmap,
      childAge
    )
    return { key: ability.key, value: val }
  })
  return values.reduce<IAbilities>((obj, next) => {
    obj[next.key] = next.value
    return obj
  }, {})
}

const getMeanValue = (
  key: string,
  currentMetrics: IMeasuredMetric[],
  currentTalentmap,
  childAge: number
) => {
  const values = currentMetrics.map(metric => {
    const transformedValue = metric.storeKey
      ?.replace(/-|\s/gi, '_')
      .toLowerCase()
    if (
      transformedValue &&
      Object.keys(currentTalentmap || {}).some(v => v === transformedValue)
    ) {
      // logger.info(`${transformedValue}: ${currentTalentmap[transformedValue]}`)
      const normValue = metricNormativeValue(
        metric,
        currentTalentmap[transformedValue],
        childAge
      )
      // add weighted value
      if (metric.weight) {
        return normValue * metric.weight[getNormWeight(childAge)]
      }
      return normValue
    }
    return -1
  })

  // logger.info(`${key}`, values)
  // for memory weighted value calculation
  if (key === 'memory' && values.length === 2) {
    const highScore = Math.max(values[0], values[1])
    const lowScore = Math.min(values[0], values[1])
    return lowScore * 0.3 + highScore * 0.7
  }

  if (!gamesInCurrentTalentMap[key] && values.length) {
    const newValues = values.filter(a => a > -1)
    return newValues.length
      ? newValues.reduce((prev, next) => prev + next, 0) / newValues.length
      : -1
  }

  return values.reduce((prev, next) => prev + next, 0)
}

export const transformMeasureMetric = (
  key: string,
  value: number,
  childAge: number,
  lockedDate?: number
) => {
  // logger.info(`${key}: ${value}`)
  const metric = allMetricswithNormData.find(m => m.storeKey === key)
  return metric
    ? metricNormativeValue(metric, value, getAge(childAge, lockedDate))
    : value
}

const metricNormativeValue = (
  metric: IMeasuredMetric,
  value: number,
  currentAge: number
) => {
  if (metric.normData) {
    const x = value / 100
    const normData = metric.normData[getNormData(currentAge)]
    // logger.info(`norm:`, normData)
    const maxZ = (1 - normData.mean) / normData.std
    const zCorrection = 1.75 - maxZ
    let Z
    if (zCorrection < 0.2) {
      Z = (x - normData.mean) / normData.std + 0.2
    } else {
      Z = (x - normData.mean) / normData.std + zCorrection
    }
    // logger.info(`Z:`, Z)
    const normInfo = normativeInfo[getStainScale(Z)]
    // logger.info(`scale:`, normInfo)
    if (normInfo.lowScoreRange === undefined) {
      return 4
    } else if (normInfo.highScoreRange === undefined) {
      return 100
    } else {
      return (
        ((Z - normInfo.lowScoreRange) /
          (normInfo.highScoreRange - normInfo.lowScoreRange)) *
          normInfo.percentail +
        normInfo.low
      )
    }
  }
  return value
}

const getStainScale = (scale: number): stainScale => {
  if (scale < -1.75) {
    return stainScale.one
  } else if (range(scale, -1.75, -1.25)) {
    return stainScale.two
  } else if (range(scale, -1.25, -0.75)) {
    return stainScale.three
  } else if (range(scale, -0.75, -0.25)) {
    return stainScale.four
  } else if (range(scale, -0.25, 0.25)) {
    return stainScale.five
  } else if (range(scale, 0.25, 0.75)) {
    return stainScale.six
  } else if (range(scale, 0.75, 1.25)) {
    return stainScale.seven
  } else if (range(scale, 1.25, 1.75)) {
    return stainScale.eight
  }
  return stainScale.nine
}

const range = (num: number, x: number, y: number) => {
  return num > x && num < y
}

const getNormData = (childAge: number) => {
  if (childAge <= 3) {
    return age.three
  } else if (childAge === 4) {
    return age.four
  } else if (childAge === 5) {
    return age.five
  } else if (childAge === 6) {
    return age.six
  }
  return age.seven
}

const getNormWeight = (childAge: number) => {
  if (childAge <= 4) {
    return age.three
  } else if (childAge === 5) {
    return age.five
  } else if (childAge === 6) {
    return age.six
  } else if (childAge === 7) {
    return age.seven
  }
  return age.eight
}



// WEBPACK FOOTER //
// ./src/services/normativeData.ts